import { runOnJS } from 'react-native-worklets';
import { memo, useCallback, useEffect } from 'react';
import Box from '../../Box/Box';
import {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import Text from '../../Text/Text';
import { HeaderTooltipProps } from './HeaderTooltip.types';

function HeaderTooltip({
  visible,
  hide,
  locNS,
  locKey,
  text,
  gradient = 'accentBackground',
  absolute = true,
}: HeaderTooltipProps) {
  const opacity = useSharedValue(0);
  const positionY = useSharedValue(0);

  const startAnimation = useCallback(() => {
    opacity.value = withSequence(
      withTiming(1, { duration: 400, easing: Easing.out(Easing.ease) }),
      withTiming(1, { duration: 3200 }),
      withTiming(0, { duration: 400, easing: Easing.in(Easing.ease) }),
    );

    positionY.value = withSequence(
      withTiming(-5, { duration: 400, easing: Easing.out(Easing.ease) }),
      withTiming(-5, { duration: 3200 }),
      withTiming(0, { duration: 400, easing: Easing.in(Easing.ease) }, () => {
        if (hide) {
          runOnJS(hide)();
        }
      }),
    );
  }, [opacity, positionY, hide]);

  const animatedStyle = useAnimatedStyle(() => {
    return {
      opacity: opacity.value,
      transform: [
        {
          translateY: positionY.value,
        },
      ],
    };
  });

  useEffect(() => {
    if (visible) {
      startAnimation();
    }
  }, [visible, startAnimation]);

  if (!visible) return null;

  const content = (
    <Box
      animatedStyle={animatedStyle}
      alignItems="center"
      variant="gradient"
      gradient={gradient}
      paddingVertical="space-050"
      paddingHorizontal="space-100"
      borderRadius="radius-100"
    >
      <Text
        variant="caption-semi"
        color="darkSurfaceAccent"
        locNS={locNS}
        locKey={locKey}
        numberOfLines={1}
      >
        {text}
      </Text>
    </Box>
  );

  return absolute ? (
    <Box
      position="absolute"
      zIndex={10}
      left="space-0"
      right="space-0"
      alignItems="center"
      justifyContent="center"
    >
      {content}
    </Box>
  ) : (
    content
  );
}

export default memo(HeaderTooltip);
